<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :closeOnEscape="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '29rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPtNoScroll"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-indigo-50 dark:bg-indigo-500/15 text-indigo-500 dark:text-indigo-400">
          <i class="pi pi-briefcase text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
          <div class="crc-form-dialog-header__subtitle">
            {{ mode === 'new' ? 'Fill in the details below' : 'Edit position details' }}
          </div>
        </div>
        <Tag
          :value="draft.department_id == null ? 'Global' : selectedDepartmentName"
          :severity="draft.department_id == null ? 'info' : 'secondary'"
          class="crc-form-dialog-header__badge"
        />
      </div>
    </template>

    <form @submit.prevent="handleSubmit">

      <!-- Name + Department -->
      <section class="crc-form-dialog-section crc-form-dialog-fields">
        <div class="flex flex-col gap-1.5">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="position-name"
              v-model="draft.name"
              :disabled="saving"
              maxlength="160"
              autocomplete="off"
              :invalid="(submitted && !draft.name?.trim()) || Boolean(nameError)"
              class="w-full rounded-md"
            />
            <label for="position-name" class="text-sm font-medium">
              Name <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1">
            Please enter a position name.
          </small>
          <small v-else-if="nameError" class="text-xs text-red-500 pl-1">
            {{ nameError }}
          </small>
        </div>

        <div class="flex flex-col gap-1.5">
          <QuickAddSelectField
            v-model="draft.department_id"
            input-id="position-department"
            label="Department"
            :options="departmentOptions"
            show-clear
            :disabled="saving || !canSetDepartment"
            :can-add="canAddDepartment && canSetDepartment"
            add-label="Create New Department"
            create-placeholder="New department name"
            :max-length="120"
            :create-option="createDepartmentOption"
          />
        </div>
      </section>

      <!-- Status -->
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div class="flex items-center gap-2.5">
            <div
              class="crc-form-dialog-status__dot"
              :class="draft.is_active ? 'crc-form-dialog-status__dot--active' : 'crc-form-dialog-status__dot--inactive'"
            />
            <div>
              <div class="crc-form-dialog-status__title">
                {{ draft.is_active ? 'Active' : 'Inactive' }}
              </div>
              <div class="crc-form-dialog-status__hint">
                {{ draft.is_active ? 'Visible in selections' : 'Hidden from selection' }}
              </div>
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <Message v-if="generalError" severity="error" :closable="false" class="mx-4 mb-3">
        {{ generalError }}
      </Message>

      <!-- Footer -->
      <div class="crc-form-dialog-footer">
        <Button
          v-if="draft.id && canDelete"
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          text
          :disabled="saving || !canDelete"
          @click="confirmDelete"
        />
        <div class="crc-form-dialog-footer__actions">
          <Button
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="onClose"
          />
          <Button
            :label="mode === 'new' ? 'Create' : 'Save'"
            :icon="mode === 'new' ? 'pi pi-plus' : 'pi pi-save'"
            :loading="saving"
            :disabled="saving || !canSubmit || !isFormValid"
            type="submit"
          />
        </div>
      </div>

    </form>
  </Dialog>
</template>

<script setup>
import { isOrgReferenceActive } from '@/features/settings/utils/settingsOrgReferenceUtils';
import { useSettingsDraftGuard } from '@/features/settings/composables/useSettingsDraftGuard';
import { computed, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import FloatLabel from 'primevue/floatlabel';
import QuickAddSelectField from '@/components/forms/QuickAddSelectField.vue';
import { crcFormDialogPtNoScroll } from '@/components/common/dialog/crcDialogPt.js';
import { useAuth } from '@/composables/useAuth';
import { useQuickAddReference } from '@/features/org-references/composables/useQuickAddReference';
import { buildQuickAddPolicy } from '@/features/org-references/config/referencePermissions';
import { normalizeReferenceId } from '@/features/org-references/utils/referenceOptionUtils';

const emit = defineEmits(['update:visible', 'save', 'delete', 'department-created']);

const props = defineProps({
  visible: { type: Boolean, default: false },
  position: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  canCreate: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
  canSetDepartment: { type: Boolean, default: false },
  departmentOptions: { type: Array, default: () => [] },
  /** `{ code, message }` from the failed save; keeps the dialog open with an inline hint. */
  saveError: { type: Object, default: null }
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v)
});

const mode = ref('edit');
const submitted = ref(false);
const dismissedError = ref(false);

const draft = reactive({
  id: null,
  name: '',
  department_id: null,
  is_active: true
});

const { isSuperAdmin, hasPermission } = useAuth();
const canAddDepartment = computed(
  () => buildQuickAddPolicy({ hasPermission, isSuperAdmin: isSuperAdmin.value }).department
);

const departmentQuickAdd = useQuickAddReference({
  entity: 'department',
  options: computed(() => props.departmentOptions),
  onCreated(option) {
    draft.department_id = normalizeReferenceId(option.id);
    // `departmentOptions` is a computed in the parents, so the quick-add upsert is
    // discarded on the next recompute — ask the owner to refetch its list.
    emit('department-created', option);
  },
});
const createDepartmentOption = departmentQuickAdd.create;

const modalTitle = computed(() => (mode.value === 'new' ? 'New Position' : 'Edit Position'));
const isFormValid = computed(() => !!draft.name?.trim());
const canSubmit = computed(() => (mode.value === 'new' ? props.canCreate : props.canEdit));

const activeError = computed(() =>
  dismissedError.value || props.saving ? null : props.saveError
);
const nameError = computed(() =>
  activeError.value?.code === 'validation.name_duplicate' ? activeError.value.message : ''
);
const generalError = computed(() =>
  activeError.value && !nameError.value ? activeError.value.message : ''
);

const selectedDepartmentName = computed(() => {
  const d = props.departmentOptions.find((item) => Number(item.id) === Number(draft.department_id));
  return d?.name || `Department #${draft.department_id}`;
});

const fillDraftFromPosition = (item) => {
  if (!item) { fillDraftNew(); return; }
  draft.id = item.id ?? null;
  draft.name = item.name ?? '';
  draft.department_id = item.department_id ?? null;
  draft.is_active = isOrgReferenceActive(item);
};

const fillDraftNew = () => {
  draft.id = null;
  draft.name = '';
  draft.department_id = null;
  draft.is_active = true;
};

watch(
  () => props.position,
  (value) => {
    if (visibleProxy.value && mode.value === 'edit') fillDraftFromPosition(value);
  },
  { deep: true }
);

watch(() => props.saveError, () => { dismissedError.value = false; });

// Editing the fields the server complained about hides the stale message.
watch(() => [draft.name, draft.department_id], () => { dismissedError.value = true; });

watch(
  () => props.visible,
  (open) => {
    if (!open) return;
    submitted.value = false;
    dismissedError.value = false;
    if (props.position?.id) {
      mode.value = 'edit';
      fillDraftFromPosition(props.position);
    } else {
      mode.value = 'new';
      fillDraftNew();
      if (props.position?.department_id != null) {
        draft.department_id = normalizeReferenceId(props.position.department_id);
      }
    }
  }
);

const onClose = () => {
  if (props.saving) return;
  visibleProxy.value = false;
  submitted.value = false;
};

// The parent closes the dialog once the request succeeds, so a failed save keeps the draft.
const handleSubmit = () => {
  if (departmentQuickAdd.isCreating.value) return;
  submitted.value = true;
  if (!isFormValid.value || !canSubmit.value || props.saving) return;
  dismissedError.value = true;
  emit('save', { ...draft });
};

const confirmDelete = () => {
  if (!draft.id || !props.canDelete || props.saving) return;
  emit('delete', { id: draft.id });
};
const draftGuard = useSettingsDraftGuard({
  draft, visible: () => props.visible, saving: () => props.saving || departmentQuickAdd.isCreating.value,
});
</script>
